import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  selectKycDocumentsLoaded,
  selectKycPhotoIdApproved,
  selectKycVerifyInProgress,
} from '@/store/selectors/kycSelectors';
import { envIsCanada } from '@/constants/constants';
import { selectIsAuthenticated } from '@/store/selectors/authSelectors';
import useVerificationMethod from '@/hooks/useVerificationMethod';

export function usePhotoIdToast(
  addToastCallback: (message: any) => void,
  cleanupToastCallback: (key: string) => void,
): void {
  const photoIdApproved = useSelector(selectKycPhotoIdApproved);
  const documentsLoaded = useSelector(selectKycDocumentsLoaded);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const TOAST_ID = 'PHOTO_ID_REQUIRED_TOAST';
  const intl = useIntl();
  const verifyInProgress = useSelector(selectKycVerifyInProgress);
  const { openVerification } = useVerificationMethod();

  const shouldPresentToast = (
    photoIdApproved: boolean,
    isAuthenticated: boolean,
    documentsLoaded: boolean,
    verifyInProgress: boolean,
  ): boolean => {
    if (!envIsCanada()) {
      return false;
    }

    if (!isAuthenticated) {
      return false;
    }

    if (!documentsLoaded) {
      return false;
    }

    if (verifyInProgress) {
      return false;
    }

    return !photoIdApproved;
  };

  useEffect(() => {
    if (!shouldPresentToast(photoIdApproved, isAuthenticated, documentsLoaded, verifyInProgress)) {
      cleanupToastCallback(TOAST_ID);
      return;
    }

    addToastCallback({
      id: TOAST_ID,
      order: 1,
      subtitle: intl.formatMessage({ id: 'toast.photoId.subtitle' }),
      cta: intl.formatMessage({ id: 'misc.verifyNow' }),
      clickCallback: openVerification,
    });
  }, [photoIdApproved, isAuthenticated, documentsLoaded, verifyInProgress]);
}
